/**
 * "Copy to scene" — the freeze (CONTENT_MODEL_SPEC §2.6, §3.2, §3.4).
 *
 * A library reference is **live**: the node's `src` is
 * `<api>/assets/{id}/file`, that 302s to the asset's current document, and
 * editing the asset changes every scene referencing it. Freezing is not a mode
 * or a flag — it is this gesture, and it works by moving bytes across the line
 * the closure rule draws: the asset folder's files are copied into the
 * *scene's* folder, and the reference dissolves into the scene's own objects.
 *
 * All of that is one viewer verb now (`viewer.unpack`, trace-viewer #811):
 * it dissolves the reference **in place** — same parent, same transform, same
 * `SceneObject` wherever the reference node survives, so selection, undo
 * entries and the inspector keep pointing at what the user clicked — stamps
 * `Provenance.placedFrom`, and handles the §3.1 leaf wrapper (a plain `.glb`
 * in the library, one node whose whole content is one file) through the
 * uploader rather than as a second code path here.
 *
 * What the sandbox still owns is the **uploader**, because only the editor
 * knows the folder: `POST /scenes/{id}/files` → the relative uri the document
 * must name the bytes by. That is {@link CmsManager.folderUploadTarget}.
 *
 * The document walk, the leaf-wrapper detection and the src rewrite used to
 * live in `copy-to-scene.ts`; every host that wanted this gesture had to
 * hand-roll them, which is why they moved into the viewer.
 */

import type { TraceViewerElement } from 'trace-viewer';
import type { SceneObject } from 'trace-viewer/scene';
import { showSpinnerToast } from '../toast';
import type { CmsManager } from './CmsManager';

const getViewer = (): TraceViewerElement | null => document.querySelector('trace-viewer');

/** Options for {@link copyReferenceToScene}. */
export interface CopyReferenceOptions {
    /**
     * What the success toast says. The default announces the gesture the user
     * asked for; the copy-on-inside-edit guard (spec §2.3 "Editing") overrides
     * it to say why a copy happened that the user did not ask for.
     */
    successMessage?: string;
}

/**
 * Replace a live library reference with the asset's content, inlined and owned
 * by this scene.
 *
 * Files the copy could **not** bring across come back on `orphaned` — the
 * content renders and this scene cannot save it — so they are surfaced rather
 * than logged past: that state is the one the reader would meet as a model
 * that vanished on reload.
 */
export async function copyReferenceToScene(
    reference: SceneObject,
    cms: CmsManager,
    options: CopyReferenceOptions = {},
): Promise<boolean> {
    const viewer = getViewer();
    if (!viewer?.traceScene) {
        console.error('[CopyToScene] No scene');
        return false;
    }

    const src = reference.src;
    if (!src || !/^https?:\/\//i.test(src)) {
        console.warn(`[CopyToScene] "${reference.name}" is not a library reference (src="${src}")`);
        return false;
    }

    const name = reference.name;
    const dismiss = showSpinnerToast(`Copying ${name} into this scene…`);
    try {
        const result = await viewer.unpack(reference, { intoFolder: cms.folderUploadTarget() });

        if (!result.unpacked) {
            dismiss(`${name} could not be copied into this scene`, 'error');
            return false;
        }

        console.log(
            `[CopyToScene] Inlined ${name}: ${result.copied.size} file(s) copied into the scene folder`,
        );

        if (result.orphaned.length > 0) {
            console.error(
                `[CopyToScene] ${result.orphaned.length} file(s) could not be copied into this scene's folder, `
                + `so a save will not carry them: ${result.orphaned.join(', ')}`,
            );
            dismiss(
                `Copied ${name}, but ${result.orphaned.length} file(s) could not be brought across — see console`,
                'error',
            );
            // The nodes ARE the scene's own now — only some bytes did not come
            // across. Reporting the copy as done is the truth an edit needs.
            return true;
        }

        dismiss(options.successMessage ?? `Copied ${name} into this scene`, 'success');
        return true;
    } catch (error) {
        console.error('[CopyToScene] Failed:', error);
        const message = error instanceof Error ? error.message : String(error);
        dismiss(`Copy to scene failed: ${message}`, 'error');
        return false;
    }
}
